/**
 * Backfill — AI look-alike sourcing for replacement hiring.
 *
 * Recruiter pastes the LinkedIn URL + a copy of the About / Experience text
 * of the employee who is leaving, and the backend returns up to 10 real
 * (Google-indexed) LinkedIn profiles similar to that person, each with a
 * match score, breakdown, and reasoning from Gemini.
 *
 * All data comes from Google Custom Search + GitHub public API — no
 * LinkedIn scraping, no ToS violation, and the whole thing runs on Google's
 * free tier at MVP scale (100 searches/day, ~25 backfill requests/day).
 */

import React, { useState, useMemo, useCallback } from 'react';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { getApiUrl } from '../../../config/api';
import { Card, CardContent, CardHeader } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Textarea } from '@/Components/ui/textarea';
import { Badge } from '@/Components/ui/badge';
import { Heading, Text } from '@/Components/ui/typography';
import { Separator } from '@/Components/ui/separator';
import { Stack, Inline } from '@/Components/ui/layout';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import {
  Sparkles, ExternalLink, Github, MapPin, Briefcase, Users, RefreshCw,
  Search, AlertCircle, CheckCircle2, Info, Bookmark, Copy,
  Zap, TrendingUp, Building2, Star, HelpCircle, Loader2, Mail, UserCheck, Link2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const MAX_PROFILE_TEXT_CHARS = 8000;

function useBackfillSearch() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const run = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const resp = await fetch(getApiUrl('/api/backfill/search'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const base = body?.error || `Search failed (HTTP ${resp.status})`;
        const detail = body?.detail ? ` — ${body.detail}` : '';
        const msg = `${base}${detail}`;
        setError(msg);
        return { ok: false, error: msg };
      }
      setResult(body);
      return { ok: true, data: body };
    } catch (err) {
      const msg = err?.message || 'Network error while searching for backfills.';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setResult(null);
  }, []);

  return { loading, error, result, run, reset };
}

// ── Sub-component: input form ─────────────────────────────────────────────

function BackfillInputForm({ onSubmit, disabled }) {
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [profileText, setProfileText] = useState('');
  const [roleHint, setRoleHint] = useState('');
  const [locationHint, setLocationHint] = useState('');
  const [experienceHint, setExperienceHint] = useState('');
  const [mustHaveSkillsRaw, setMustHaveSkillsRaw] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const textLength = profileText.trim().length;
  const linkedinValid = useMemo(() => {
    const s = linkedinUrl.trim().toLowerCase();
    return s.includes('linkedin.com/in/');
  }, [linkedinUrl]);

  // Paste is optional: with link enrichment on, the backend pulls the profile
  // from the URL. A valid LinkedIn URL is all that's required to search.
  const canSubmit = linkedinValid && !disabled;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      linkedin_url: linkedinUrl.trim(),
      profile_text: profileText.trim(),
      role_hint: roleHint.trim() || null,
      location_hint: locationHint.trim() || null,
      experience_years_hint: experienceHint.trim() ? Number(experienceHint) : null,
      must_have_skills: mustHaveSkillsRaw
        .split(',').map(s => s.trim()).filter(Boolean),
    });
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <Heading level="h3">Find Matching Candidates</Heading>
            <Text variant="muted" size="sm" className="mt-1">
              Enter the LinkedIn profile URL — we'll pull the profile from the
              link. Optionally paste the About and Experience to sharpen the
              match. Receive up to ten professionals matched on role, skills,
              and seniority.
            </Text>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <Stack spacing="md">
            <div>
              <Label htmlFor="backfill-url" className="mb-2 block">
                LinkedIn profile URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="backfill-url"
                type="url"
                placeholder="https://www.linkedin.com/in/john-doe/"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                disabled={disabled}
                autoComplete="off"
                spellCheck={false}
              />
              {linkedinUrl && !linkedinValid && (
                <Text variant="destructive" size="xs" className="mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Must be a linkedin.com/in/... profile URL
                </Text>
              )}
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <Label htmlFor="backfill-text">
                  About & Experience{' '}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground flex items-center gap-1 cursor-help">
                        <HelpCircle className="w-3 h-3" />
                        Why?
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      LinkedIn restricts programmatic profile access. Manually copying
                      the public summary lets the system build accurate search queries
                      while remaining fully compliant with LinkedIn's terms of service.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Textarea
                id="backfill-text"
                placeholder="Paste the About and Experience sections from the LinkedIn profile."
                value={profileText}
                onChange={(e) => setProfileText(e.target.value.slice(0, MAX_PROFILE_TEXT_CHARS))}
                disabled={disabled}
                rows={7}
                className="font-mono text-sm"
              />
              <div className="flex justify-between mt-1">
                <Text size="xs" variant="muted">
                  {textLength > 0
                    ? `${textLength.toLocaleString()} characters`
                    : "Optional — leave blank to pull it from the link, or paste it to sharpen the match."}
                </Text>
                <Text size="xs" variant="muted">
                  {MAX_PROFILE_TEXT_CHARS.toLocaleString()} maximum
                </Text>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-sm text-primary underline-offset-2 hover:underline flex items-center gap-1"
              >
                <Info className="w-3.5 h-3.5" />
                {showAdvanced ? 'Hide' : 'Show'} advanced filters
              </button>
            </div>

            {showAdvanced && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
                <div>
                  <Label htmlFor="backfill-role" className="mb-2 block">Target role</Label>
                  <Input
                    id="backfill-role"
                    placeholder="e.g. Senior Backend Engineer"
                    value={roleHint}
                    onChange={(e) => setRoleHint(e.target.value)}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <Label htmlFor="backfill-location" className="mb-2 block">Preferred location</Label>
                  <Input
                    id="backfill-location"
                    placeholder="e.g. Bangalore"
                    value={locationHint}
                    onChange={(e) => setLocationHint(e.target.value)}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <Label htmlFor="backfill-exp" className="mb-2 block">Years of experience</Label>
                  <Input
                    id="backfill-exp"
                    type="number"
                    step="0.5"
                    min="0"
                    max="40"
                    placeholder="e.g. 5"
                    value={experienceHint}
                    onChange={(e) => setExperienceHint(e.target.value)}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <Label htmlFor="backfill-skills" className="mb-2 block">
                    Must-have skills (comma separated)
                  </Label>
                  <Input
                    id="backfill-skills"
                    placeholder="Python, PostgreSQL, Kafka"
                    value={mustHaveSkillsRaw}
                    onChange={(e) => setMustHaveSkillsRaw(e.target.value)}
                    disabled={disabled}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
              <Button
                type="submit"
                disabled={!canSubmit}
                size="lg"
                className="gap-2"
              >
                {disabled ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Searching
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Find Matching Candidates
                  </>
                )}
              </Button>
              <Text variant="muted" size="xs">
                Typically completes in 20–30 seconds. Limited to 20 searches per day.
              </Text>
            </div>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Sub-component: single candidate card ──────────────────────────────────

function CandidateResultCard({ candidate, rank }) {
  const [saving, setSaving] = useState(false);

  const scoreColor = useMemo(() => {
    const s = candidate.match_score ?? 0;
    if (s >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (s >= 60) return 'text-amber-600 bg-amber-50 border-amber-200';
    if (s >= 40) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-slate-500 bg-slate-50 border-slate-200';
  }, [candidate.match_score]);

  const breakdown = candidate.score_breakdown || {};
  const bd = [
    { label: 'Role', value: breakdown.role, max: 30 },
    { label: 'Skills', value: breakdown.skills, max: 30 },
    { label: 'Seniority', value: breakdown.seniority, max: 15 },
    { label: 'Industry', value: breakdown.industry, max: 15 },
    { label: 'Location', value: breakdown.location, max: 10 },
  ].filter((row) => row.value != null);

  // Own-pool candidates (from the company's existing applicants) have no public
  // LinkedIn URL — they're reached via the email/résumé already on file. External
  // candidates (PDL / public web) are reached via their LinkedIn profile.
  const isInternal = candidate.source === 'internal'
    || (candidate.sources || []).includes('internal');
  const email = candidate.email || null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await fetch(getApiUrl('/api/backfill/save'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          linkedin_url: candidate.linkedin_url || null,
          email: candidate.email || null,
          source: candidate.source || null,
          name: candidate.name,
          headline: candidate.headline,
        }),
      });
      if (resp.ok) {
        toast.success(`Saved ${candidate.name || 'candidate'} to your shortlist`);
      } else {
        toast.error('Could not save this candidate — try again.');
      }
    } catch {
      toast.error('Save failed — network error.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(candidate.linkedin_url || '');
      toast.success('LinkedIn URL copied');
    } catch {
      toast.error('Could not copy URL.');
    }
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email || '');
      toast.success('Email copied');
    } catch {
      toast.error('Could not copy email.');
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-muted-foreground">#{rank}</span>
              <Heading level="h4" className="truncate">
                {candidate.name || 'Unknown candidate'}
              </Heading>
              {isInternal && (
                <Badge variant="secondary" className="shrink-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                  <UserCheck className="h-3 w-3 mr-1" />
                  In your pool
                </Badge>
              )}
              {candidate.github?.html_url && (
                <a
                  href={candidate.github.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                  title="GitHub profile found"
                >
                  <Github className="h-4 w-4" />
                </a>
              )}
            </div>
            {candidate.headline && (
              <Text size="sm" className="text-muted-foreground truncate">
                {candidate.headline}
              </Text>
            )}
          </div>

          <div className={cn(
            'flex flex-col items-center justify-center rounded-lg border px-3 py-2 min-w-[70px]',
            scoreColor
          )}>
            <span className="text-2xl font-bold leading-none tabular-nums">
              {candidate.match_score ?? 0}
            </span>
            <span className="text-[10px] uppercase tracking-wide font-medium mt-0.5">
              match
            </span>
          </div>
        </div>

        {bd.length > 0 && (
          <div className="mt-3 grid grid-cols-5 gap-2">
            {bd.map((row) => (
              <div key={row.label} className="text-center">
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary"
                    style={{ width: `${Math.min(100, (row.value / row.max) * 100)}%` }}
                  />
                </div>
                <Text size="xs" variant="muted" className="mt-1 truncate">
                  {row.label}
                </Text>
              </div>
            ))}
          </div>
        )}

        {candidate.snippet_summary && (
          <Text size="sm" className="mt-3 text-muted-foreground italic line-clamp-2">
            "{candidate.snippet_summary}"
          </Text>
        )}

        {(candidate.why_similar?.length > 0) && (
          <div className="mt-3">
            <Text size="xs" className="font-semibold text-green-700 mb-1 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Why similar
            </Text>
            <ul className="text-sm text-foreground/80 space-y-0.5 list-disc list-inside">
              {candidate.why_similar.slice(0, 4).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        )}

        {(candidate.why_different?.length > 0) && (
          <div className="mt-2">
            <Text size="xs" className="font-semibold text-amber-700 mb-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> Where they differ
            </Text>
            <ul className="text-sm text-foreground/70 space-y-0.5 list-disc list-inside">
              {candidate.why_different.slice(0, 3).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        )}

        <Separator className="my-4" />

        <div className="flex flex-wrap items-center gap-2">
          {candidate.linkedin_url ? (
            <>
              <a
                href={candidate.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0a66c2] hover:bg-[#004182] text-white text-sm font-medium transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View LinkedIn
              </a>
              <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
                <Copy className="h-3.5 w-3.5" />
                Copy URL
              </Button>
            </>
          ) : email ? (
            <>
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors"
              >
                <Mail className="h-3.5 w-3.5" />
                Email
              </a>
              <Button variant="outline" size="sm" onClick={handleCopyEmail} className="gap-1.5">
                <Copy className="h-3.5 w-3.5" />
                Copy email
              </Button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Already in your candidate pool</span>
          )}
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
            <Bookmark className="h-3.5 w-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Sub-component: target summary ─────────────────────────────────────────

function TargetProfileSummary({ target, targetSource }) {
  if (!target) return null;
  const skills = target.core_skills || [];
  return (
    <Card className="bg-muted/30 border-dashed">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-primary/10">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Text size="xs" variant="muted" className="uppercase tracking-wide">
                Target profile
              </Text>
              {targetSource === 'enriched' && (
                <Badge
                  variant="secondary"
                  className="bg-sky-50 text-sky-700 border-sky-200"
                  title="We pulled this profile straight from the LinkedIn link"
                >
                  <Link2 className="h-3 w-3 mr-1" />
                  Pulled from the link
                </Badge>
              )}
            </div>
            <Heading level="h4">{target.current_role || 'Unknown role'}</Heading>
            <Inline spacing="sm" className="mt-2 flex-wrap">
              {target.seniority_band && (
                <Badge variant="secondary" className="capitalize">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {target.seniority_band}
                </Badge>
              )}
              {target.estimated_years_experience != null && (
                <Badge variant="secondary">
                  <Briefcase className="h-3 w-3 mr-1" />
                  {target.estimated_years_experience} yrs
                </Badge>
              )}
              {target.primary_industry && (
                <Badge variant="secondary">
                  <Building2 className="h-3 w-3 mr-1" />
                  {target.primary_industry}
                </Badge>
              )}
              {target.location && target.location !== 'unknown' && (
                <Badge variant="secondary">
                  <MapPin className="h-3 w-3 mr-1" />
                  {target.location}
                </Badge>
              )}
            </Inline>
            {skills.length > 0 && (
              <div className="mt-3">
                <Text size="xs" variant="muted" className="mb-1">Core skills</Text>
                <Inline spacing="xs" className="flex-wrap">
                  {skills.slice(0, 10).map((s) => (
                    <Badge key={s} variant="outline">
                      <Star className="h-3 w-3 mr-1 text-amber-500" />
                      {s}
                    </Badge>
                  ))}
                </Inline>
              </div>
            )}
            {target.career_summary && (
              <Text size="sm" className="mt-3 italic text-muted-foreground">
                {target.career_summary}
              </Text>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function BackfillPage() {
  const { loading, error, result, run, reset } = useBackfillSearch();

  const candidates = result?.candidates || [];
  const target = result?.target || null;
  const meta = result?.meta || null;

  const handleReset = () => {
    reset();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <EmployerLayout>
      <Stack spacing="lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Heading level="h1">Talent Match</Heading>
            <Text variant="muted" className="mt-1 max-w-2xl">
              Provide a LinkedIn profile and receive up to ten matched professionals
              — scored, reasoned, and ready for outreach.
            </Text>
          </div>
          {result && (
            <Button variant="outline" onClick={handleReset} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              New Search
            </Button>
          )}
        </div>

        {!result && (
          <BackfillInputForm onSubmit={run} disabled={loading} />
        )}

        {loading && (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
              <Text className="font-medium">Analysing profile and matching candidates</Text>
              <Text variant="muted" size="sm" className="mt-1">
                Typically completes in 20–30 seconds. Running multi-angle searches and
                scoring each match against the target profile.
              </Text>
            </CardContent>
          </Card>
        )}

        {error && !loading && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <Text className="font-semibold text-destructive">Search failed</Text>
                <Text size="sm" className="mt-1">{error}</Text>
                <Button variant="outline" size="sm" onClick={handleReset} className="mt-3">
                  Try again
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {result && !loading && (
          <>
            <TargetProfileSummary target={target} targetSource={meta?.target_source} />

            {candidates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Zap className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                  <Heading level="h4">No matches found</Heading>
                  <Text variant="muted" size="sm" className="mt-1 max-w-md mx-auto">
                    Broaden the criteria — clear the location, remove must-have skills,
                    or specify a different target role in advanced filters.
                  </Text>
                  <Button variant="outline" onClick={handleReset} className="mt-4">
                    Refine search
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <Heading level="h3">
                    {candidates.length} candidate{candidates.length === 1 ? '' : 's'} found
                  </Heading>
                  {meta?.total_pool > candidates.length && (
                    <Text variant="muted" size="sm">
                      Ranked top {candidates.length} from a pool of {meta.total_pool}.
                    </Text>
                  )}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {candidates.map((c, i) => (
                    <CandidateResultCard
                      key={c.linkedin_url || i}
                      candidate={c}
                      rank={i + 1}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Stack>
    </EmployerLayout>
  );
}
